import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, X, Loader2, CalendarOff, Package, Wrench, Hammer, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import JobTemplatesManager from "@/components/app/JobTemplatesManager";
import JobPhotoBoard from "@/components/app/JobPhotoBoard";
import AiQuoteDraft from "@/components/app/AiQuoteDraft";

const emptyJob = {
  client_name: "",
  client_phone: "",
  address_from: "",
  address_to: "",
  floor_from: "",
  floor_to: "",
  elevator_from: false,
  elevator_to: false,
  deposit_paid_at: "",
  deposit_receipt_received: false,
  scheduled_date: new Date().toISOString().slice(0, 10),
  depot_time: "",
  start_time: "08:00",
  end_time: "",
  assigned_employee_ids: [],
  foreman_id: null,
  items: [],
  materials: "",
  notes: "",
  status: "programmato",
  total_amount: "",
  settle_amount: "",
  vehicle_id: null,
  job_type: "",
};

const CATEGORIES = [
  { value: "altro", label: "Altro" },
  { value: "mobili", label: "Mobili" },
  { value: "elettrodomestici", label: "Elettrodomestici" },
  { value: "scatole", label: "Scatole" },
];

const JOB_TYPES = [
  { value: "monolocale", label: "🏠 Monolocale" },
  { value: "bilocale", label: "🏠 Bilocale" },
  { value: "trilocale", label: "🏠 Trilocale" },
  { value: "quadrilocale", label: "🏠 Quadrilocale" },
  { value: "appartamento_grande", label: "🏘️ Appartamento grande (5+)" },
  { value: "villa", label: "🏡 Villa" },
  { value: "ufficio", label: "🏢 Ufficio" },
  { value: "negozio", label: "🏪 Negozio" },
  { value: "deposito", label: "📦 Deposito/Magazzino" },
  { value: "garage", label: "🅿️ Garage/Cantina" },
  { value: "altro", label: "📋 Altro" },
];

const EUR = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });

function FloorFields({ side, form, update }) {
  const f = `floor_${side}`, el = `elevator_${side}`;
  return (
    <div className="mt-2 flex items-center gap-3">
      <Input value={form[f] || ""} onChange={(e) => update(f, e.target.value)} required placeholder="Piano (es. 3, terra)" className="w-40" aria-label="Piano" data-testid={`job-floor-${side}-input`} />
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={!!form[el]} onChange={(e) => update(el, e.target.checked)} className="w-4 h-4" data-testid={`job-elevator-${side}-checkbox`} />
        Ascensore
      </label>
    </div>
  );
}

export default function JobForm({ job, onDone, onCancel }) {
  const [form, setForm] = useState(() => {
    if (!job) return emptyJob;
    return {
      ...emptyJob,
      ...job,
      total_amount: job.total_amount ?? "",
      settle_amount: job.settle_amount ?? "",
      materials: job.materials ?? "",
      floor_from: job.floor_from ?? "",
      floor_to: job.floor_to ?? "",
      elevator_from: !!job.elevator_from,
      elevator_to: !!job.elevator_to,
      deposit_paid_at: job.deposit_paid_at ?? "",
      deposit_receipt_received: !!job.deposit_receipt_received,
      foreman_id: job.foreman_id ?? null,
    };
  });
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [leaveConflicts, setLeaveConflicts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [invoiceFiles, setInvoiceFiles] = useState([]); // File[] - uploaded after job is created
  const [newItem, setNewItem] = useState({ name: "", quantity: 1, note: "", category: "altro", smontaggio: false, rimontaggio: false });

  useEffect(() => {
    api.get("/employees").then((r) =>
      setEmployees(r.data.filter((e) => e.role === "dipendente" && e.active !== false))
    );
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.scheduled_date) return;
    api.get("/leave-requests/conflicts", { params: { date: form.scheduled_date } })
      .then((r) => setLeaveConflicts(r.data))
      .catch(() => setLeaveConflicts([]));
  }, [form.scheduled_date]);

  const conflictIds = new Set(leaveConflicts.map((c) => c.user_id));
  const assignedEmployees = employees.filter((e) => form.assigned_employee_ids.includes(e.id));

  // If foreman_id gets removed from assigned list → clear it
  useEffect(() => {
    if (form.foreman_id && !form.assigned_employee_ids.includes(form.foreman_id)) {
      setForm((f) => ({ ...f, foreman_id: null }));
    }
  }, [form.assigned_employee_ids]); // eslint-disable-line

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleEmp = (id) =>
    update(
      "assigned_employee_ids",
      form.assigned_employee_ids.includes(id)
        ? form.assigned_employee_ids.filter((x) => x !== id)
        : [...form.assigned_employee_ids, id]
    );

  const addItem = () => {
    if (!newItem.name.trim()) return;
    const toAdd = { ...newItem, quantity: Number(newItem.quantity) || 1 };
    // Scatole non ha smontaggio/rimontaggio
    if (toAdd.category === "scatole") {
      toAdd.smontaggio = false;
      toAdd.rimontaggio = false;
    }
    update("items", [...form.items, toAdd]);
    setNewItem({ name: "", quantity: 1, note: "", category: "altro", smontaggio: false, rimontaggio: false });
  };

  const removeItem = (idx) => update("items", form.items.filter((_, i) => i !== idx));

  const toggleItemFlag = (idx, flag) =>
    update(
      "items",
      form.items.map((it, i) => (i === idx ? { ...it, [flag]: !it[flag] } : it))
    );

  const [lastDraft, setLastDraft] = useState(null);
  const plural = (n, one, many) => (n ? `${n} ${n === 1 ? one : many}` : `? ${many}`);
  const applyDraft = (d) => {
    const crewLine = d.crew_size || d.vans || d.estimated_hours
      ? `Squadra consigliata: ${plural(d.crew_size, "persona", "persone")}, ${plural(d.vans, "furgone", "furgoni")}, circa ${d.estimated_hours || "?"} ore`
      : "";
    const prev = lastDraft;
    setLastDraft({ items: d.items || [], noteLines: [d.notes, crewLine].filter(Boolean) });
    setForm((f) => {
      const pick = (k) => d[k] || f[k];
      const baseItems = prev ? f.items.filter((it) => !prev.items.includes(it)) : f.items;
      const baseNotes = prev ? f.notes.split("\n").filter((l) => !prev.noteLines.some((p) => p.split("\n").includes(l))).join("\n") : f.notes;
      const notes = [baseNotes, d.notes, crewLine].filter(Boolean).join("\n");
      return {
        ...f,
        client_name: pick("client_name"), client_phone: pick("client_phone"),
        address_from: pick("address_from"), address_to: pick("address_to"),
        scheduled_date: pick("scheduled_date"), start_time: pick("start_time"),
        job_type: pick("job_type"), materials: pick("materials"),
        items: [...baseItems, ...(d.items || [])], notes,
      };
    });
  };

  const total = Number(form.total_amount) || 0;
  const settle = Number(form.settle_amount) || 0;
  const acconto = total - settle;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        total_amount: form.total_amount === "" ? null : Number(form.total_amount),
        settle_amount: form.settle_amount === "" ? null : Number(form.settle_amount),
      };
      let result;
      if (job?.id) {
        const { data } = await api.put(`/jobs/${job.id}`, payload);
        result = data;
        toast.success("Lavoro aggiornato");
      } else {
        const { data } = await api.post("/jobs", payload);
        result = data;
        toast.success("Lavoro creato");
      }
      // Upload invoices (after job is created/updated)
      if (invoiceFiles.length > 0 && result?.id) {
        let ok = 0;
        for (const f of invoiceFiles) {
          const fd = new FormData();
          fd.append("file", f);
          try {
            await api.post(`/jobs/${result.id}/invoices`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            ok++;
          } catch (err) {
            toast.error(`${f.name}: ${formatApiError(err)}`);
          }
        }
        if (ok > 0) toast.success(`${ok} fattur${ok === 1 ? "a caricata" : "e caricate"}`);
        setInvoiceFiles([]);
      }
      onDone?.(result);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="job-form">
      {/* Templates picker - solo per nuovo lavoro */}
      {!job?.id && (
        <AiQuoteDraft onApply={applyDraft} onPrice={(v) => { update("total_amount", v); toast.success("Prezzo inserito nel totale"); }} />
      )}
      {!job?.id && (
        <div className="bg-indigo-50/60 border border-indigo-200 rounded-2xl p-4">
          <JobTemplatesManager
            pickOnly
            onPick={(t) => {
              setForm((f) => ({
                ...f,
                materials: t.materials || f.materials,
                items: [...(f.items || []), ...(t.items || [])],
                notes: t.notes ? (f.notes ? `${f.notes}\n\n${t.notes}` : t.notes) : f.notes,
              }));
              toast.success(`Modello "${t.name}" applicato`);
            }}
          />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Nome cliente</Label>
          <Input value={form.client_name} onChange={(e) => update("client_name", e.target.value)} required data-testid="job-client-name-input" />
        </div>
        <div>
          <Label>Telefono cliente</Label>
          <Input value={form.client_phone} onChange={(e) => update("client_phone", e.target.value)} data-testid="job-client-phone-input" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Indirizzo partenza</Label>
          <Input value={form.address_from} onChange={(e) => update("address_from", e.target.value)} required placeholder="Via Roma 1, Milano" data-testid="job-address-from-input" />
          <FloorFields side="from" form={form} update={update} />
        </div>
        <div>
          <Label>Indirizzo arrivo</Label>
          <Input value={form.address_to} onChange={(e) => update("address_to", e.target.value)} required placeholder="Via Dante 5, Torino" data-testid="job-address-to-input" />
          <FloorFields side="to" form={form} update={update} />
        </div>
      </div>

      <div>
        <Label className="flex items-center gap-1">📐 <span>Tipo trasloco</span></Label>
        <Select value={form.job_type || ""} onValueChange={(v) => update("job_type", v)}>
          <SelectTrigger data-testid="job-type-select"><SelectValue placeholder="Seleziona tipo (per confronto tempi)…" /></SelectTrigger>
          <SelectContent>
            {JOB_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-500 mt-1">
          Il tipo serve per confrontare la durata di questo lavoro con la media degli altri dello stesso tipo. Se ci metterai più del +30% riceverai un avviso.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div>
          <Label>Data</Label>
          <Input type="date" value={form.scheduled_date} onChange={(e) => update("scheduled_date", e.target.value)} required data-testid="job-date-input" />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Label className="flex items-center gap-1">
            🏭 <span>Ora deposito</span>
          </Label>
          <Input
            type="time"
            value={form.depot_time}
            onChange={(e) => update("depot_time", e.target.value)}
            placeholder="07:30"
            data-testid="job-depot-time-input"
            className="bg-amber-50 border-amber-200"
          />
        </div>
        <div>
          <Label>Ora inizio</Label>
          <Input type="time" value={form.start_time} onChange={(e) => update("start_time", e.target.value)} required data-testid="job-start-time-input" />
        </div>
        <div>
          <Label>Ora fine</Label>
          <Input type="time" value={form.end_time} onChange={(e) => update("end_time", e.target.value)} data-testid="job-end-time-input" />
        </div>
        <div>
          <Label>Stato</Label>
          <Select value={form.status} onValueChange={(v) => update("status", v)}>
            <SelectTrigger data-testid="job-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="programmato">Programmato</SelectItem>
              <SelectItem value="in_corso">In Corso</SelectItem>
              <SelectItem value="completato">Completato</SelectItem>
              <SelectItem value="annullato">Annullato</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 -mt-2">
        🏭 <b>Ora deposito</b>: è l'orario in cui i dipendenti devono trovarsi in deposito prima di partire verso il cliente.
      </p>

      <div>
        <Label>Dipendenti assegnati</Label>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3">
          {employees.length === 0 && (
            <div className="col-span-full text-sm text-slate-400 py-2">
              Nessun dipendente disponibile. Aggiungine uno nella sezione Dipendenti.
            </div>
          )}
          {employees.map((e) => {
            const onLeave = conflictIds.has(e.id);
            return (
              <label
                key={e.id}
                className={`flex items-center gap-2 bg-white border rounded-lg px-3 py-2 cursor-pointer transition ${
                  onLeave ? "border-rose-300 bg-rose-50 hover:border-rose-400" : "border-slate-200 hover:border-sky-400"
                }`}
                data-testid={`assign-employee-${e.id}`}
              >
                <Checkbox checked={form.assigned_employee_ids.includes(e.id)} onCheckedChange={() => toggleEmp(e.id)} />
                <span className={`text-sm font-medium truncate flex-1 ${onLeave ? "text-rose-900" : "text-slate-800"}`}>{e.name}</span>
                {onLeave && (
                  <span title="In riposo in questa data" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 bg-rose-100 border border-rose-200 rounded-full px-2 py-0.5">
                    <CalendarOff className="w-3 h-3" /> Riposo
                  </span>
                )}
              </label>
            );
          })}
        </div>
        {leaveConflicts.length > 0 && (
          <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            ⚠️ In questa data sono in riposo: <b>{leaveConflicts.map((c) => c.user_name).join(", ")}</b>
          </div>
        )}
      </div>

      {/* Foto materiali da utilizzare (solo in modifica lavoro esistente) */}
      {job?.id && (
        <JobPhotoBoard
          job={job}
          kind="materials"
          title="Foto materiali da utilizzare"
          subtitle="Carica le foto dei materiali che portate per questo trasloco (scatoloni, pluriball, cinghie…)."
          canUpload={true}
          onChanged={(updated) => { /* no-op: updates persist on job doc */ }}
          colorScheme="indigo"
        />
      )}
      {!job?.id && (
        <div className="bg-indigo-50/60 border border-dashed border-indigo-200 rounded-xl px-4 py-3 text-xs text-indigo-700">
          💡 Dopo aver salvato il lavoro potrai caricare qui le <b>foto dei materiali da utilizzare</b>.
        </div>
      )}

      {/* Caposquadra */}
      <div>
        <Label>Nomina Caposquadra</Label>
        <Select
          value={form.foreman_id || "none"}
          onValueChange={(v) => update("foreman_id", v === "none" ? null : v)}
          disabled={assignedEmployees.length === 0}
        >
          <SelectTrigger data-testid="job-foreman-select">
            <SelectValue placeholder={assignedEmployees.length === 0 ? "Seleziona prima i dipendenti" : "— Nessuno —"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Nessuno —</SelectItem>
            {assignedEmployees.map((e) => (
              <SelectItem key={e.id} value={e.id}>👷 {e.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-400 mt-1">Il caposquadra può essere scelto solo tra i dipendenti assegnati.</p>
      </div>

      <div>
        <Label>Mezzo utilizzato (opz.)</Label>
        <Select value={form.vehicle_id || "none"} onValueChange={(v) => update("vehicle_id", v === "none" ? null : v)}>
          <SelectTrigger data-testid="job-vehicle-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Nessuno —</SelectItem>
            {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate} · {v.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Materiali */}
      <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4" data-testid="job-materials-section">
        <Label className="flex items-center gap-1.5 text-amber-900 font-bold text-sm"><Wrench className="w-4 h-4 text-amber-700" /> Note materiali — cosa i dipendenti devono portare</Label>
        <p className="text-[11px] text-amber-800/80 mb-2">Scrivi qui tutto quello che la squadra deve avere con sé (scatoloni, nastro, pluriball, cinghie, attrezzi…). Appare in grande nei dettagli del lavoro.</p>
        <Textarea
          rows={3}
          value={form.materials}
          onChange={(e) => update("materials", e.target.value)}
          placeholder="Es: 20 scatoloni grandi · 10 scatoloni medi · nastro da imballaggio · pluriball · 2 cinghie · coperte imbottite · trapano · chiave inglese"
          data-testid="job-materials-input"
          className="mt-1 bg-white"
        />
      </div>

      {/* Oggetti con smontaggio/rimontaggio */}
      <div>
        <Label>Oggetti da traslocare</Label>
        <div className="mt-2 space-y-2">
          {form.items.map((it, idx) => {
            const isScatole = it.category === "scatole";
            const Icon = isScatole ? Package : it.category === "elettrodomestici" ? Hammer : it.category === "mobili" ? Wrench : Package;
            return (
              <div key={idx} className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 space-y-2">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-sky-600 shrink-0" />
                  <span className="font-mono text-xs text-sky-600 font-bold w-10">×{it.quantity}</span>
                  <span className="flex-1 font-medium text-slate-800">{it.name}</span>
                  <span className="text-[10px] uppercase font-bold tracking-wider bg-slate-100 text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">
                    {CATEGORIES.find((c) => c.value === it.category)?.label || "Altro"}
                  </span>
                  {it.note && <span className="hidden md:inline text-xs text-slate-500 italic">{it.note}</span>}
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="text-slate-400 hover:text-rose-600"
                    data-testid={`remove-item-${idx}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {!isScatole && (
                  <div className="flex flex-wrap gap-2 pl-6">
                    <label className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border cursor-pointer transition ${it.smontaggio ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "bg-white border-slate-200 text-slate-500 hover:border-indigo-300"}`} data-testid={`item-${idx}-smontaggio`}>
                      <Checkbox checked={it.smontaggio} onCheckedChange={() => toggleItemFlag(idx, "smontaggio")} />
                      <Wrench className="w-3 h-3" /> Smontaggio
                    </label>
                    <label className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border cursor-pointer transition ${it.rimontaggio ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-slate-200 text-slate-500 hover:border-emerald-300"}`} data-testid={`item-${idx}-rimontaggio`}>
                      <Checkbox checked={it.rimontaggio} onCheckedChange={() => toggleItemFlag(idx, "rimontaggio")} />
                      <Hammer className="w-3 h-3" /> Rimontaggio
                    </label>
                  </div>
                )}
              </div>
            );
          })}

          {/* Nuovo oggetto */}
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-3 space-y-2">
            <div className="flex flex-col md:flex-row gap-2">
              <Input
                placeholder="Es: Divano letto 3 posti"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem())}
                className="flex-1"
                data-testid="new-item-name-input"
              />
              <Input
                type="number"
                min="1"
                value={newItem.quantity}
                onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                className="w-full md:w-20"
                data-testid="new-item-quantity-input"
              />
              <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v, smontaggio: v === "scatole" ? false : newItem.smontaggio, rimontaggio: v === "scatole" ? false : newItem.rimontaggio })}>
                <SelectTrigger className="w-full md:w-40" data-testid="new-item-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Note (opz.)"
                value={newItem.note}
                onChange={(e) => setNewItem({ ...newItem, note: e.target.value })}
                className="w-full md:w-32"
                data-testid="new-item-note-input"
              />
            </div>
            {newItem.category !== "scatole" && (
              <div className="flex flex-wrap gap-2">
                <label className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border cursor-pointer transition ${newItem.smontaggio ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "bg-white border-slate-200 text-slate-500 hover:border-indigo-300"}`} data-testid="new-item-smontaggio">
                  <Checkbox checked={newItem.smontaggio} onCheckedChange={(v) => setNewItem({ ...newItem, smontaggio: !!v })} />
                  <Wrench className="w-3 h-3" /> Smontaggio
                </label>
                <label className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border cursor-pointer transition ${newItem.rimontaggio ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-slate-200 text-slate-500 hover:border-emerald-300"}`} data-testid="new-item-rimontaggio">
                  <Checkbox checked={newItem.rimontaggio} onCheckedChange={(v) => setNewItem({ ...newItem, rimontaggio: !!v })} />
                  <Hammer className="w-3 h-3" /> Rimontaggio
                </label>
              </div>
            )}
            <div className="flex justify-end">
              <Button type="button" onClick={addItem} variant="outline" data-testid="add-item-button">
                <Plus className="w-4 h-4 mr-1" /> Aggiungi oggetto
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Totali */}
      <div className="bg-gradient-to-br from-emerald-50 to-sky-50 border border-emerald-200 rounded-2xl p-5" data-testid="job-totals-section">
        <div className="flex items-center gap-2 mb-3 text-emerald-800 font-display font-bold">
          💰 Importi del lavoro
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Totale lavoro (€)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.total_amount}
              onChange={(e) => update("total_amount", e.target.value)}
              placeholder="0.00"
              className="mt-1 bg-white"
              data-testid="job-total-amount-input"
            />
            <p className="text-[11px] text-slate-500 mt-1">Prezzo pieno concordato con il cliente</p>
          </div>
          <div>
            <Label>Da saldare al termine (€)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.settle_amount}
              onChange={(e) => update("settle_amount", e.target.value)}
              placeholder="0.00"
              className="mt-1 bg-white"
              data-testid="job-settle-amount-input"
            />
            <p className="text-[11px] text-slate-500 mt-1">Quanto deve consegnare il cliente a fine trasloco</p>
          </div>
        </div>
        {total > 0 && settle >= 0 && settle <= total && (
          <div className="mt-3 flex flex-wrap items-center gap-3 pt-3 border-t border-emerald-200 text-sm">
            <span className="text-slate-500">Totale: <b className="text-slate-900">{EUR.format(total)}</b></span>
            <span className="text-slate-300">·</span>
            <span className="text-slate-500">Acconto (già incassato): <b className="text-slate-900">{EUR.format(acconto > 0 ? acconto : 0)}</b></span>
            <span className="text-slate-300">·</span>
            <span className="text-emerald-700">Saldo: <b>{EUR.format(settle)}</b></span>
          </div>
        )}
        {acconto > 0 && (
          <div className="mt-3 grid sm:grid-cols-2 gap-3 pt-3 border-t border-emerald-200" data-testid="job-deposit-section">
            <div>
              <Label>Acconto versato il</Label>
              <Input type="date" value={form.deposit_paid_at || ""} onChange={(e) => update("deposit_paid_at", e.target.value)} className="mt-1 bg-white" data-testid="job-deposit-date-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 sm:mt-6">
              <input type="checkbox" checked={!!form.deposit_receipt_received} onChange={(e) => update("deposit_receipt_received", e.target.checked)} className="w-4 h-4" data-testid="job-deposit-receipt-checkbox" />
              Contabile ricevuta (uso interno)
            </label>
          </div>
        )}
      </div>

      {/* Fattura upload */}
      <div className="bg-white border-2 border-dashed border-slate-300 rounded-2xl p-5" data-testid="job-invoice-upload-section">
        <div className="flex items-center gap-2 mb-3 text-slate-700 font-display font-bold">
          <FileText className="w-5 h-5 text-rose-600" /> Fatture (opz.)
        </div>
        <p className="text-xs text-slate-500 mb-3">Allega PDF, JPG o PNG delle fatture emesse. Verranno caricate dopo il salvataggio del lavoro.</p>
        <Input
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          multiple
          onChange={(e) => setInvoiceFiles(Array.from(e.target.files || []))}
          data-testid="job-invoice-file-input"
          className="cursor-pointer file:bg-slate-900 file:text-white file:border-0 file:px-3 file:py-1.5 file:rounded-md file:mr-3 file:cursor-pointer"
        />
        {invoiceFiles.length > 0 && (
          <ul className="mt-3 space-y-1" data-testid="job-invoice-pending-list">
            {invoiceFiles.map((f, idx) => (
              <li key={idx} className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
                <span className="truncate font-mono text-slate-700">📎 {f.name} <span className="text-slate-400">({Math.round(f.size / 1024)}KB)</span></span>
                <button
                  type="button"
                  onClick={() => setInvoiceFiles((arr) => arr.filter((_, i) => i !== idx))}
                  className="text-slate-400 hover:text-rose-600"
                  data-testid={`remove-invoice-pending-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <Label>Note aggiuntive</Label>
        <Textarea
          rows={3}
          value={form.notes}
          onChange={(e) => update("notes", e.target.value)}
          placeholder="Istruzioni particolari, accessi, ascensore, parcheggio…"
          data-testid="job-notes-input"
        />
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} data-testid="job-cancel-button">
            Annulla
          </Button>
        )}
        <Button
          type="submit"
          disabled={saving}
          className="bg-sky-600 hover:bg-sky-700 text-white"
          data-testid="job-submit-button"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {job?.id ? "Salva Modifiche" : "Crea Lavoro"}
        </Button>
      </div>
    </form>
  );
}
